/**
 * 決定論的乱数（xoshiro128**）
 *
 * `Math.random()` は使わない。シードと消費回数だけで状態が完全に再現できることが、
 * リプレイ・オンライン同期・自動テスト・不具合再現のすべての前提になる。
 * 詳細は docs/02-データ設計.md §4「決定論性」。
 *
 * 状態は 32bit 符号なし整数 4 語。GameState にそのまま埋め込んで直列化できる。
 */

/** xoshiro128** の内部状態。すべて uint32。 */
export type RngState = readonly [number, number, number, number];

const rotl = (x: number, k: number): number => ((x << k) | (x >>> (32 - k))) >>> 0;

/** 文字列シードを 4 語に展開する（splitmix32）。全ゼロ状態は避ける。 */
export function seedRng(seed: string): RngState {
  // 文字列 → 32bit（FNV-1a）
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }

  // splitmix32 で 4 語に展開
  const words: number[] = [];
  let x = h >>> 0;
  for (let i = 0; i < 4; i++) {
    x = (x + 0x9e3779b9) >>> 0;
    let z = x;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad) >>> 0;
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97) >>> 0;
    words.push((z ^ (z >>> 15)) >>> 0);
  }

  const state = words as [number, number, number, number];
  // 全ゼロだと xoshiro が退化する
  if ((state[0] | state[1] | state[2] | state[3]) === 0) return [1, 2, 3, 4];
  return state;
}

/** 次の uint32 と、進めた後の状態を返す。純粋関数。 */
export function nextU32(s: RngState): { value: number; state: RngState } {
  const [s0, s1, s2, s3] = s;

  const value = (Math.imul(rotl(Math.imul(s1, 5) >>> 0, 7), 9) >>> 0) >>> 0;

  const t = (s1 << 9) >>> 0;
  let n2 = (s2 ^ s0) >>> 0;
  let n3 = (s3 ^ s1) >>> 0;
  const n1 = (s1 ^ n2) >>> 0;
  const n0 = (s0 ^ n3) >>> 0;
  n2 = (n2 ^ t) >>> 0;
  n3 = rotl(n3, 11);

  return { value, state: [n0, n1, n2, n3] };
}

/** 0 以上 max 未満の整数。剰余バイアスを除去してある。 */
export function nextInt(s: RngState, max: number): { value: number; state: RngState } {
  if (max <= 0) throw new RangeError(`nextInt: max must be positive, got ${max}`);
  // 2^32 を max で割り切れる範囲に収まるまで引き直す
  const limit = Math.floor(0x100000000 / max) * max;
  let state = s;
  for (;;) {
    const r = nextU32(state);
    state = r.state;
    if (r.value < limit) return { value: r.value % max, state };
  }
}

/**
 * 高速な可変版。モンテカルロなど大量に回す用途のみで使う。
 * `core` のルール処理では純粋関数版（nextU32 / nextInt）を使うこと。
 */
export class FastRng {
  private s0: number;
  private s1: number;
  private s2: number;
  private s3: number;

  /**
   * シードは文字列か、`seedRng()` が返した4語の状態。
   *
   * **不正なシードは黙って通さない。** 数値をそのまま渡すと
   * `typeof seed === 'string'` が偽になり、`seed[0]` が undefined になって
   * 内部状態が NaN で埋まる。以降 `u32()` は同じ値を返し続け、
   * 「乱数が死んでいる」のに例外は出ない。
   *
   * これは実際に踏んだ。AI の勝率見積りが 1.000 と 0.000 しか返さなくなり、
   * 標本数を増やしても変わらないという形で現れた。決定論を土台にしている以上、
   * 乱数が死んだことは**その場で落として**気づけるようにする。
   * 型検査はテストには効かない（tsconfig の対象外）ので、実行時に見る。
   */
  constructor(seed: string | RngState) {
    if (typeof seed !== 'string' && !Array.isArray(seed)) {
      throw new TypeError(
        `FastRng: シードは文字列か seedRng() の返り値。${typeof seed} を受け取った` +
          `（数値を渡していないか。String(n) にすれば意図どおりに動く）`,
      );
    }
    const st = typeof seed === 'string' ? seedRng(seed) : seed;
    if (st.length !== 4 || st.some((w) => !Number.isFinite(w))) {
      throw new TypeError(`FastRng: 状態は有限な4語でなければならない。received ${JSON.stringify(st)}`);
    }
    this.s0 = st[0];
    this.s1 = st[1];
    this.s2 = st[2];
    this.s3 = st[3];
  }

  /** 現在の状態を取り出す（純粋関数版へ引き継げる） */
  snapshot(): RngState {
    return [this.s0, this.s1, this.s2, this.s3];
  }

  u32(): number {
    const value = Math.imul(rotl(Math.imul(this.s1, 5) >>> 0, 7), 9) >>> 0;
    const t = (this.s1 << 9) >>> 0;
    this.s2 = (this.s2 ^ this.s0) >>> 0;
    this.s3 = (this.s3 ^ this.s1) >>> 0;
    this.s1 = (this.s1 ^ this.s2) >>> 0;
    this.s0 = (this.s0 ^ this.s3) >>> 0;
    this.s2 = (this.s2 ^ t) >>> 0;
    this.s3 = rotl(this.s3, 11);
    return value;
  }

  int(max: number): number {
    const limit = Math.floor(0x100000000 / max) * max;
    for (;;) {
      const v = this.u32();
      if (v < limit) return v % max;
    }
  }
}
